<template>
  <section  id="PartCollections"
            class="mobile-part-collections-card mx-1">
    <section class="mobile-part-collections-toolbar mb-3">
      <p class="is-size-6 has-text-weight-bold mb-1">{{ `${$tc('partCollection', 2)} (${totalCount})`  }}</p>
      <div class="control has-icons-left search-control">
        <input :value="search"
               :placeholder="`${$t('searchContent', { content: $tc('part', 2) })}...`"
               type="text"
               class="input search-input"
               @input="event => $emit('search-input', event)">
        <span class="icon is-left fas fa-search" />
        <span v-if="search" class="search-right-meta">
          <span class="search-results-count has-text-grey">
            {{ searchResultsCount }} {{ $tc('result', searchResultsCount) }}
          </span>
          <button type="button"
                  class="search-clear-button"
                  @click="$emit('clear-search')">
            <i class="fas fa-times" />
          </button>
        </span>
      </div>
    </section>

    <section class="pb-2">
      <div v-for="collection in collections"
           :id="`part-collection-${collection.id}`"
           :key="collection.rowKey">
        <part-collection-block :can-add-all-to-cart="isWidget ? isWidgetCartEnabled : isCartOrErpCartEnabled"
                               :collection="collection"
                               :column-widths="columnWidths"
                               :content-width="contentWidth"
                               :expanded="getExpanded(collection)"
                               :is-expanding-all="isExpandingAll"
                               :is-search-mode="isSearchMode"
                               :is-searching="isSearching"
                               :part-columns="effectivePartColumns"
                               :has-hide-price="hasHidePrice"
                               :hide-thumbnail="true"
                               :is-widget="isWidget"
                               style="margin-right: 0 !important;"
                               @addToCart="$emit('add-to-cart', $event)"
                               @add-all-to-cart="$emit('add-all-to-cart', $event)"
                               @export-parts="$emit('export-parts', $event)"
                               @toggle="$emit('toggle-collection', collection.id)"
                               @toggle-show-retail-price="$emit('toggle-show-retail-price', collection.id)"
                               @toggle-show-wholesale-price="$emit('toggle-show-wholesale-price', collection.id)"
                               @toggle-show-discount-price="$emit('toggle-show-discount-price', collection.id)"/>
      </div>
    </section>

    <div v-if="hasMoreCollections && !isSearchMode"
         ref="sentinel"
         class="load-more-sentinel"/>

    <BomAddToCartModal v-if="addToCartModalOpen && !!cartItem"
                       :bom-item="cartItem"
                       :is-widget-cart-enabled="isWidgetCartEnabled"
                       @add-to-cart="$emit('cart-submit', $event)"
                       @cancel="$emit('close-cart-modal')"
                       @close="$emit('close-cart-modal')"/>
  </section>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import PartCollectionBlock from './PartCollectionBlock.vue'
import BomAddToCartModal from '@/components/library/content/asset/viewer/illustrationViewer/bom/BomAddToCartModal.vue'

import type { DisplayCollection, PartRow } from '@/interfaces/library/partCollection'

const props = defineProps<{
  search: string
  searchResultsCount: number
  isSearching: boolean
  isSearchMode: boolean
  isExpandingAll: boolean
  collections: DisplayCollection[]
  columnWidths: Record<string, number>
  contentWidth: number
  effectivePartColumns: string[]
  isCartOrErpCartEnabled: boolean
  addToCartModalOpen: boolean
  cartItem: unknown
  isWidgetCartEnabled: boolean
  hasHidePrice: boolean
  hasMoreCollections: boolean
  getExpanded: (collection: DisplayCollection) => boolean
  totalCount: number
  isWidget: boolean
}>()

const emit = defineEmits<{
  (e: 'expand-all'): void
  (e: 'collapse-all'): void
  (e: 'search-input', event: Event): void
  (e: 'clear-search'): void
  (e: 'toggle-collection', collectionId: number): void
  (e: 'add-to-cart', payload: PartRow): void
  (e: 'add-all-to-cart', payload: PartRow[]): void
  (e: 'export-parts', collectionId: number): void
  (e: 'cart-submit', qty: number): void
  (e: 'close-cart-modal'): void
  (e: 'toggle-show-retail-price', collectionId: number): void
  (e: 'toggle-show-wholesale-price', collectionId: number): void
  (e: 'toggle-show-discount-price', collectionId: number): void
  (e: 'load-more'): void
}>()

const sentinel = ref<HTMLElement | null>(null)

watch(sentinel, (el, _, onCleanup) => {
  if (!el) return
  const observer = new IntersectionObserver(
    entries => { if (entries[0].isIntersecting) emit('load-more') },
    { threshold: 0.1 }
  )
  observer.observe(el)
  onCleanup(() => observer.disconnect())
})

defineExpose({ scrollRef: null })
</script>

<style scoped>
.mobile-part-collections-card {
  display: flex;
  flex-direction: column;
}

.mobile-part-collections-toolbar {
  display: flex;
  flex-direction: column;
}

.search-control {
  position: relative;
  width: 100%;
}

.search-input {
  padding-right: 9rem;
}

.search-right-meta {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.search-results-count {
  font-size: 0.875rem;
  white-space: nowrap;
  pointer-events: none;
}

.search-clear-button {
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: #7a7a7a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.search-clear-button:hover,
.search-clear-button:focus,
.search-clear-button:active {
  color: #363636;
  outline: none;
}

.load-more-sentinel {
  height: 1px;
}
</style>
